import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import TokenEntry from '../components/evaluation/TokenEntry';
import EvaluationForm from '../components/evaluation/EvaluationForm';
import CompletionPage from '../components/evaluation/CompletionPage';
import ConsentModal from '../components/ConsentModal';
import { evaluationAPI, handleApiError } from '../services/api';
import toast from 'react-hot-toast';

const EvaluationPage = () => {
  const [searchParams] = useSearchParams();
  const [currentStep, setCurrentStep] = useState('token'); // 'token', 'consent', 'form', 'complete'
  const [evaluationData, setEvaluationData] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showConsentModal, setShowConsentModal] = useState(false);

  // Check if token is provided in URL
  useEffect(() => {
    const tokenFromUrl = searchParams.get('token');
    if (tokenFromUrl) {
      validateToken(tokenFromUrl);
    }
  }, [searchParams]);

  const validateToken = async (token) => {
    setLoading(true);
    try {
      // First validate token and get existing responses
      const tokenValidation = await evaluationAPI.validateToken(token);
      
      // Then get questions for the family's template
      const questionsResponse = await evaluationAPI.getQuestions(token);
      
      setEvaluationData({
        token: token,
        familyMember: questionsResponse.data.familyMember,
        family: questionsResponse.data.family,
        template: questionsResponse.data.template,
        existingResponses: tokenValidation.data.existingResponses || []
      });
      
      setQuestions(questionsResponse.data.categories || []);
      
      // Show consent modal instead of going directly to form
      setCurrentStep('consent');
      setShowConsentModal(true);
      toast.success(`Welcome, ${questionsResponse.data.familyMember.firstName}!`);
    } catch (error) {
      handleApiError(error, 'Invalid or expired token');
    } finally {
      setLoading(false);
    }
  };

  const handleTokenSubmit = (token) => {
    validateToken(token);
  };

  const handleConsentAccept = async () => {
    // Record consent in backend (uncomment when backend is deployed)
    /*
    try {
      await evaluationAPI.recordConsent({
        token: evaluationData.token,
        consentGiven: true,
        consentTimestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Failed to record consent:', error);
    }
    */
    
    setShowConsentModal(false);
    setCurrentStep('form');
  };

  const handleConsentDecline = () => {
    setShowConsentModal(false);
    setCurrentStep('token');
    setEvaluationData(null);
    toast.error('You must provide consent to continue with the evaluation.');
  };

  const handleEvaluationComplete = () => {
    setCurrentStep('complete');
  };

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 'token':
        return (
          <TokenEntry 
            onTokenSubmit={handleTokenSubmit}
            loading={loading}
          />
        );
      case 'form':
        return (
          <EvaluationForm 
            evaluationData={evaluationData}
            questions={questions}
            onComplete={handleEvaluationComplete}
          />
        );
      case 'complete':
        return (
          <CompletionPage 
            familyMember={evaluationData?.familyMember}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen relative bg-cream">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary-50/40 via-cream to-secondary-50/40"></div>
      
      <div className="relative py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back to home link */}
          <Link
            to="/"
            className="inline-flex items-center text-sm text-secondary-500 hover:text-primary-600 transition-colors duration-200 mb-6 group"
          >
            <svg className="w-4 h-4 mr-1.5 group-hover:-translate-x-0.5 transition-transform" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
            Back to Home
          </Link>
          {renderCurrentStep()}
        </div>
      </div>

      {/* Consent Modal */}
      <ConsentModal
        isOpen={showConsentModal}
        onAccept={handleConsentAccept}
        onDecline={handleConsentDecline}
        familyMember={evaluationData?.familyMember}
        family={evaluationData?.family}
      />
    </div>
  );
};

export default EvaluationPage;